// SPDX-License-Identifier: AGPL-3.0-or-later
// Based on au-births by Ben Richardson — https://benrichardson.dev
// Pure layout maths shared by the SVG charts and exercised by positional tests.
// No DOM, no side effects — every function here is deterministic and unit-tested.

export interface Bin { x0: number; x1: number; count: number }
export interface Cell { row: number; col: number; x: number; y: number; w: number; h: number }

/** A linear scale mapping [d0,d1] → [r0,r1]. Degenerate domains map to r0. */
export function linScale(d0: number, d1: number, r0: number, r1: number): (v: number) => number {
  const span = d1 - d0;
  if (span === 0 || !Number.isFinite(span)) return () => r0;
  return (v: number) => r0 + ((v - d0) / span) * (r1 - r0);
}

/** "Nice" axis ticks covering [min,max] with roughly `count` steps. */
export function niceTicks(min: number, max: number, count = 5): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) return [min];
  const span = max - min;
  const raw = span / Math.max(1, count);
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const step = (norm >= 5 ? 5 : norm >= 2 ? 2 : 1) * mag;
  const start = Math.ceil(min / step) * step;
  const ticks: number[] = [];
  for (let t = start; t <= max + step * 1e-6; t += step) ticks.push(Math.round(t / step) * step);
  return ticks;
}

/**
 * Histogram binning over `binCount` equal-width, contiguous bins spanning the
 * data range (or [domainMin,domainMax] if given). Bins tile the range flush with
 * no gaps or overlap; the last bin is right-closed so the maximum value lands in
 * it; counts of finite values sum to the number of finite inputs.
 */
export function binValues(values: number[], binCount: number, domainMin?: number, domainMax?: number): { bins: Bin[]; min: number; max: number } {
  const finite = values.filter((v) => Number.isFinite(v));
  const min = domainMin ?? (finite.length ? Math.min(...finite) : 0);
  const rawMax = domainMax ?? (finite.length ? Math.max(...finite) : 1);
  const n = Math.max(1, Math.floor(binCount));
  // On a degenerate (all-equal) domain, widen to a unit span so bins stay ordered;
  // the effective top edge is min+span, kept consistent with the returned `max`.
  const span = rawMax - min || 1;
  const max = min + span;
  const width = span / n;
  const bins: Bin[] = Array.from({ length: n }, (_, i) => ({ x0: min + i * width, x1: min + (i + 1) * width, count: 0 }));
  bins[n - 1].x1 = max; // guard float drift so the top edge is exactly `max`
  for (const v of finite) {
    let idx = Math.floor((v - min) / width);
    if (idx < 0) idx = 0;
    if (idx >= n) idx = n - 1; // include the maximum in the last (right-closed) bin
    bins[idx].count++;
  }
  return { bins, min, max };
}

/**
 * Row-major grid of `cols`×`rows` cells tiling a `width`×`height` box with an
 * optional uniform `gap` between cells. Cells stay in-bounds and never overlap;
 * with gap=0 they tile the box flush.
 */
export function gridCells(cols: number, rows: number, width: number, height: number, gap = 0): Cell[] {
  const c = Math.max(0, Math.floor(cols));
  const r = Math.max(0, Math.floor(rows));
  const cells: Cell[] = [];
  if (c === 0 || r === 0) return cells;
  const cw = (width - gap * (c - 1)) / c;
  const ch = (height - gap * (r - 1)) / r;
  for (let row = 0; row < r; row++) {
    for (let col = 0; col < c; col++) {
      cells.push({ row, col, x: col * (cw + gap), y: row * (ch + gap), w: Math.max(0, cw), h: Math.max(0, ch) });
    }
  }
  return cells;
}

/**
 * Cumulative stacking of parallel series. Given `series[s][i]` returns bands
 * `bands[s][i] = { lo, hi }` where hi−lo = the value and bands are contiguous
 * (band s starts where band s−1 ended) with no overlap. Nulls count as 0.
 */
export function stackBands(series: (number | null)[][], length: number): { lo: number; hi: number }[][] {
  const bands: { lo: number; hi: number }[][] = series.map(() => []);
  for (let i = 0; i < length; i++) {
    let acc = 0;
    for (let s = 0; s < series.length; s++) {
      const v = series[s][i];
      const val = v != null && Number.isFinite(v) ? v : 0;
      bands[s].push({ lo: acc, hi: acc + val });
      acc += val;
    }
  }
  return bands;
}
